import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from './Modal';
import Spinner from './Spinner';
import { toast } from './Toast';
import { exportCSV, printTable } from './ExportButton';
import EditableSelect from './EditableSelect';

const CATEGORIES = [
  { id:'Asigurari',   label:'🛡️ Asigurări',   tipuri:['RCA','CASCO'],                              areExpirare:true },
  { id:'Autorizari',  label:'📋 Autorizări',   tipuri:['ITP','Vigneta','Rovinieta','Altele'],       areExpirare:true },
  { id:'Service',     label:'🔧 Service',      tipuri:['Tractare','Reparatie','Revizie','Altele'],  areExpirare:false },
  { id:'Vulcanizare', label:'🔵 Vulcanizare',  tipuri:['Schimb anvelope','Echilibrare','Pana','Altele'], areExpirare:false },
];

const EMPTY = { nrInmatriculare:'', tipDocument:'', nrDoc:'', dataDoc:'', dataExpirare:'', valoare:'', inlocuita:'' };

function zileColor(zile) { return zile<0?'tr-expirat':zile<=30?'tr-expira':''; }
function badge(zile) {
  if (zile<0) return <span className="badge badge-danger">Expirat {Math.abs(zile)}z</span>;
  if (zile<=30) return <span className="badge badge-warning">{zile} zile</span>;
  return <span className="badge badge-ok">{zile} zile</span>;
}
function FilterInput({ value, onChange }) {
  return <input value={value} onChange={e=>onChange(e.target.value)} placeholder="Filtrează..."
    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}/>;
}
function fmt(val) {
  return parseFloat(val||0).toLocaleString('ro-RO', {minimumFractionDigits:2, maximumFractionDigits:2});
}
function esteInlocuit(r) { return parseInt(r.inlocuita) > 0; }

export default function CheltuieliTab() {
  const isDriver = (localStorage.getItem('rol') || '') === 'conducator_auto';
  const [tab, setTab] = useState('Asigurari');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nruri, setNruri] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ col:'dataDoc', dir:'desc' });
  const [selectedRow, setSelectedRow] = useState(null);
  const [pulseId, setPulseId] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [reinnoireModal, setReinnoireModal] = useState(null);
  const [reinnoireForm, setReinnoireForm] = useState({});
  const username = localStorage.getItem('username') || '';

  const [importModal, setImportModal] = useState(false);
  const [importErori, setImportErori] = useState(null);
  const [importErrorMsg, setImportErrorMsg] = useState(null);
  const [importSuccessMsg, setImportSuccessMsg] = useState(null);
  const [importing, setImporting] = useState(false);
  const [tooltipVizibil, setTooltipVizibil] = useState(null);
  const fileInputRef = useRef(null);

  const deschideImport = () => {
    setImportErori(null); setImportErrorMsg(null); setImportSuccessMsg(null);
    setImportModal(true);
  };

  const descarcaMacheta = async () => {
    try {
      const r = await api.get(`/api/cheltuieli/import/macheta?document=${tab}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `macheta_import_${tab.toLowerCase()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast('Nu s-a putut descărca macheta', 'error');
    }
  };

  const incarcaMacheta = () => fileInputRef.current?.click();

  const onFisierSelectat = async (e) => {
    const fisier = e.target.files?.[0];
    e.target.value = '';
    if (!fisier) return;

    setImporting(true);
    setImportErori(null); setImportErrorMsg(null); setImportSuccessMsg(null);
    try {
      const fd = new FormData();
      fd.append('fisier', fisier);
      fd.append('document', tab);
      fd.append('operare', username);
      const r = await api.post('/api/cheltuieli/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportSuccessMsg(`${r.data.inserate} document(e) importat(e) cu succes!`);
      load();
    } catch (e) {
      if (e.response?.data?.erori) {
        setImportErrorMsg(e.response.data.error);
        setImportErori(e.response.data.erori);
      } else {
        setImportErrorMsg(e.response?.data?.error || e.message);
      }
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => { api.get('/api/auto/nruri').then(r=>setNruri(r.data.map(x=>x.nrInmatriculare))); }, []);

  const load = useCallback(() => {
    setLoading(true); setSelectedRow(null); setPulseId(null);
    api.get(`/api/cheltuieli/rcaITP?document=${tab}`).then(r=>setRows(r.data)).finally(()=>setLoading(false));
  }, [tab]);

  useEffect(() => { load(); setFilters({}); setDateFrom(''); setDateTo(''); }, [load]);

  const catInfo = CATEGORIES.find(c=>c.id===tab);
  const setFilter = (col,val) => setFilters(prev=>({...prev,[col]:val}));
  const toggleSort = (col) => setSort(prev=>({col,dir:prev.col===col&&prev.dir==='asc'?'desc':'asc'}));
  const sortIcon = (col) => sort.col===col?(sort.dir==='asc'?' ▲':' ▼'):' ⇅';

  const filtered = rows.filter(r=>{
    if (dateFrom && r.dataDoc && r.dataDoc < dateFrom) return false;
    if (dateTo && r.dataDoc && r.dataDoc > dateTo) return false;
    return Object.entries(filters).every(([col,val])=>!val||String(r[col]||'').toLowerCase().includes(val.toLowerCase()));
  });

  const sorted = [...filtered].sort((a,b)=>{
    const av=String(a[sort.col]||''), bv=String(b[sort.col]||'');
    return sort.dir==='asc'?av.localeCompare(bv):bv.localeCompare(av);
  });

  const totalValoare = filtered.reduce((s,r)=>s+parseFloat(r.valoare||0),0);

  // Documente active (fara bifa de inlocuit) din categoria curenta, pentru selectorul
  // "Inlocuieste documentul" - calculat direct din randurile deja incarcate (fara cerere noua).
  const activeDocs = rows.filter(r => !esteInlocuit(r) && !(modal?.mode==='edit' && r.nrCrt===modal.id));

  const selectRow = (row) => {
    const isSel = selectedRow?.nrCrt === row.nrCrt;
    if (isSel) { setSelectedRow(null); setPulseId(null); return; }
    setSelectedRow(row);
    // Cauta documentul vechi pe care randul selectat il inlocuieste (daca il inlocuieste)
    const parinte = rows.find(r => parseInt(r.inlocuita) === row.nrCrt);
    setPulseId(parinte ? parinte.nrCrt : null);
    setPulseKey(k => k+1);
  };

  const openAdd = () => {
    setForm(nruri.length === 1 ? { ...EMPTY, nrInmatriculare: nruri[0] } : { ...EMPTY });
    setModal({mode:'add'});
  };
  const openEdit = (row) => { setForm({...row}); setModal({mode:'edit', id:row.nrCrt}); };

  const save = async () => {
    if (modal.mode==='add') {
      if (!form.nrInmatriculare || !form.tipDocument || !String(form.nrDoc||'').trim() ||
          !form.dataDoc || !String(form.valoare||'').trim() ||
          (catInfo.areExpirare && !form.dataExpirare)) {
        toast('Completează toate câmpurile.', 'error'); return;
      }
    }
    try {
      const payload = {...form, document:tab, operare:username};
      if (modal.mode==='add') await api.post('/api/cheltuieli/rcaITP', payload);
      else await api.put(`/api/cheltuieli/rcaITP/${modal.id}`, payload);
      setModal(null); load();
      toast(modal.mode==='add'?'Înregistrare adăugată!':'Înregistrare actualizată!');
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Ștergi înregistrarea?')) return;
    try { await api.delete(`/api/cheltuieli/rcaITP/${id}`); load(); toast('Înregistrare ștearsă!'); }
    catch(e) { toast('Eroare la ștergere','error'); }
  };

  const openReinnoire = (row) => {
    setReinnoireForm({ nrDoc:'', dataDoc:'', dataExpirare:'', valoare:'' });
    setReinnoireModal(row);
  };

  const saveReinnoire = async () => {
    try {
      await api.post(`/api/cheltuieli/rcaITP/${reinnoireModal.nrCrt}/reinnoire`,
        { ...reinnoireForm, operare: username });
      setReinnoireModal(null);
      load();
      toast('Document reînnoit cu succes!');
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  // Cand aleg un document de inlocuit din lista, blochez si precompletez Nr. Auto / Tip document
  const onSelectInlocuieste = (id) => {
    if (!id) { setForm({...form, inlocuita:''}); return; }
    const doc = activeDocs.find(d => String(d.nrCrt)===String(id));
    if (doc) setForm({...form, inlocuita:id, nrInmatriculare:doc.nrInmatriculare, tipDocument:doc.tipDocument});
    else setForm({...form, inlocuita:id});
  };
  const inlocuiesteBlocat = modal?.mode==='add' && !!form.inlocuita;

  const SH = ({col,children}) => (
    <th onClick={()=>toggleSort(col)} style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}}>
      {children}{sortIcon(col)}
    </th>
  );

  return (
    <div>
      <div className="tabs">
        {CATEGORIES.map(c=>(
          <button key={c.id} className={`tab-btn ${tab===c.id?'active':''}`} onClick={()=>setTab(c.id)}>{c.label}</button>
        ))}
      </div>

      {/* Filtru data PE UN RAND */}
      <div style={{background:'#f7f9fc',padding:'8px 14px',borderBottom:'1px solid #e0e8f0',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,fontWeight:'bold',color:'#555',whiteSpace:'nowrap'}}>Perioadă:</span>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
          style={{padding:'4px 8px',fontSize:12,border:'1px solid #ccc',borderRadius:4,width:150}}/>
        <span style={{color:'#888'}}>—</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
          style={{padding:'4px 8px',fontSize:12,border:'1px solid #ccc',borderRadius:4,width:150}}/>
        {(dateFrom||dateTo) && <button className="btn btn-sm" style={{background:'#eee'}} onClick={()=>{setDateFrom('');setDateTo('');}}>✕</button>}
        <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
          <span style={{fontSize:12,color:'#555'}}>{filtered.length} înreg.</span>
          <span style={{fontWeight:'bold',color:'#1a3a5c',fontSize:12}}>Total: {fmt(totalValoare)} RON</span>
          <button className="btn btn-sm" style={{background:'#217346',color:'white'}} onClick={()=>exportCSV(sorted,tab.toLowerCase())}>📊 CSV</button>
          <button className="btn btn-sm" style={{background:'#555',color:'white'}} onClick={()=>printTable(catInfo.label,sorted)}>🖨️</button>
          {!isDriver && selectedRow && <button className="btn btn-warning btn-sm" onClick={()=>openEdit(selectedRow)}>✏️ Modifică</button>}
          {!isDriver && selectedRow && catInfo.areExpirare && !esteInlocuit(selectedRow) &&
            <button className="btn btn-sm" style={{background:'#2d6ca2',color:'white'}} onClick={()=>openReinnoire(selectedRow)}>🔄 Reînnoire</button>}
          {!isDriver && selectedRow && <button className="btn btn-danger btn-sm" onClick={()=>del(selectedRow.nrCrt)}>🗑️</button>}
          <button className="btn btn-success btn-sm" onClick={openAdd}>+ Adaugă</button>
        </div>
      </div>

      {loading?<Spinner/>:(
        <div style={{overflowX:'auto'}}>
          <table>
            <thead>
              <tr>
                <SH col="nrInmatriculare">Nr. Auto</SH>
                <SH col="tipDocument">Tip document</SH>
                <SH col="nrDoc">Nr. Doc</SH>
                <SH col="dataDoc">Data doc</SH>
                {catInfo.areExpirare && <SH col="dataExpirare">Expiră</SH>}
                {catInfo.areExpirare && <th>Zile rămase</th>}
                <SH col="valoare">Valoare (RON)</SH>
                <th>Înlocuit</th>
              </tr>
              <tr style={{background:'#f0f4fa'}}>
                <th>
                  <select value={filters.nrInmatriculare||''} onChange={e=>setFilter('nrInmatriculare',e.target.value)}
                    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                    <option value="">Toate</option>
                    {nruri.map(n=><option key={n} value={n}>{n}</option>)}
                  </select>
                </th>
                <th><FilterInput value={filters.tipDocument||''} onChange={v=>setFilter('tipDocument',v)}/></th>
                <th><FilterInput value={filters.nrDoc||''} onChange={v=>setFilter('nrDoc',v)}/></th>
                <th></th>
                {catInfo.areExpirare && <th></th>}
                {catInfo.areExpirare && <th></th>}
                <th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r=>{
                const esteInl = esteInlocuit(r);
                const zile = catInfo.areExpirare && r.dataExpirare && !esteInl
                  ? Math.round((new Date(r.dataExpirare)-new Date())/86400000)
                  : null;
                const isSel = selectedRow?.nrCrt === r.nrCrt;
                const doPuls = pulseId === r.nrCrt;
                return (
                  <tr key={r.nrCrt} onClick={()=>selectRow(r)}
                    className={esteInl ? '' : zileColor(zile)}
                    style={{cursor:'pointer',background:isSel?'#e8f0fe':(esteInl?'#f8f8f8':undefined),outline:isSel?'2px solid #2d6ca2':'none',opacity:esteInl?0.6:1}}>
                    <td><b>{r.nrInmatriculare}</b></td>
                    <td>{r.tipDocument}</td>
                    <td>{r.nrDoc}</td>
                    <td>{r.dataDoc}</td>
                    {catInfo.areExpirare && <td>{r.dataExpirare}</td>}
                    {catInfo.areExpirare && <td>
                      <span key={doPuls?`p${pulseKey}`:'np'} className={doPuls?'pulse-inlocuit':''}>
                        {esteInl
                          ? <span className="badge" style={{background:'#eee',color:'#888'}}>Înlocuit</span>
                          : (zile!==null?badge(zile):'')}
                      </span>
                    </td>}
                    <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.valoare)}</td>
                    <td style={{textAlign:'center'}}>
                      <span key={doPuls?`p2${pulseKey}`:'np2'} className={doPuls?'pulse-inlocuit':''}>
                        {esteInl?'✅':''}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {sorted.length===0&&<tr><td colSpan={catInfo.areExpirare?8:6} style={{textAlign:'center',padding:20,color:'#888'}}>Fără date</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selectedRow && (
        <div style={{padding:'6px 14px',background:'#e8f0fe',fontSize:12,color:'#1a3a5c',borderTop:'1px solid #c0d0e8'}}>
          Selectat: <b>{selectedRow.nrInmatriculare}</b> — {selectedRow.tipDocument} — {selectedRow.dataDoc}
        </div>
      )}

      {modal && (
        <Modal title={modal.mode==='add'?`Adaugă ${catInfo.label}`:`Editează (ID: ${modal.id})`}
          onClose={()=>setModal(null)} onSave={save}>

          {modal.mode==='add' && catInfo.areExpirare && (
            <div className="form-row single" style={{marginBottom:8}}>
              <button type="button" className="btn btn-sm" style={{background:'#2d6ca2',color:'white'}} onClick={deschideImport}>
                📥 Import (mai multe documente deodată)
              </button>
            </div>
          )}

          {modal.mode==='add' && catInfo.areExpirare && (
            <div className="form-row single" style={{marginBottom:8}}>
              <div className="form-group">
                <label>Înlocuiește documentul</label>
                <select value={form.inlocuita||''} onChange={e=>onSelectInlocuieste(e.target.value)}>
                  <option value="">-- Nu înlocuiește nimic --</option>
                  {activeDocs.map(d=>(
                    <option key={d.nrCrt} value={d.nrCrt}>
                      {d.nrInmatriculare} — {d.tipDocument} — {d.nrDoc} — expiră: {d.dataExpirare}
                    </option>
                  ))}
                </select>
                {activeDocs.length===0 && (
                  <span style={{fontSize:11,color:'#888'}}>Nu există documente active în această categorie</span>
                )}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Nr. Auto<span style={{color:'#e74c3c'}}> *</span></label>
              <EditableSelect value={form.nrInmatriculare} options={nruri} disabled={inlocuiesteBlocat}
                onChange={v=>setForm({...form,nrInmatriculare:v})}/>
            </div>
            <div className="form-group">
              <label>Tip document<span style={{color:'#e74c3c'}}> *</span></label>
              <EditableSelect value={form.tipDocument} options={catInfo.tipuri} disabled={inlocuiesteBlocat}
                onChange={v=>setForm({...form,tipDocument:v})}/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Nr. Document<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="text" value={form.nrDoc||''} onChange={e=>setForm({...form,nrDoc:e.target.value})}/>
            </div>
            <div className="form-group"><label>Valoare (RON)<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={form.valoare||''} onChange={e=>setForm({...form,valoare:e.target.value})}/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Data document<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={form.dataDoc||''} onChange={e=>setForm({...form,dataDoc:e.target.value})}/>
            </div>
            {catInfo.areExpirare
              ? <div className="form-group"><label>Data expirare<span style={{color:'#e74c3c'}}> *</span></label>
                  <input type="date" value={form.dataExpirare||''} onChange={e=>setForm({...form,dataExpirare:e.target.value})}/>
                </div>
              : <div></div>
            }
          </div>

          {modal.mode==='edit' && catInfo.areExpirare && (
            <div className="form-row single">
              <div className="form-group">
                <label>Înlocuiește documentul</label>
                <select value={form.inlocuita||''} onChange={e=>setForm({...form,inlocuita:e.target.value})}>
                  <option value="">-- Nu înlocuiește nimic --</option>
                  {activeDocs.map(d=>(
                    <option key={d.nrCrt} value={d.nrCrt}>
                      {d.tipDocument} — {d.nrDoc} — expiră: {d.dataExpirare}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}

      {importModal && (
        <Modal title={`Import ${catInfo.label} din Excel`} onClose={()=>setImportModal(false)}>
          <input ref={fileInputRef} type="file" accept=".xlsx" style={{display:'none'}} onChange={onFisierSelectat}/>

          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <button type="button" className="btn" style={{background:'#217346',color:'white',flex:1}} onClick={descarcaMacheta}>
                📄 Descarcă machetă
              </button>
              <span style={{position:'relative'}}>
                <button type="button" className="btn btn-sm" style={{borderRadius:'50%',width:26,height:26,padding:0}}
                  onClick={()=>setTooltipVizibil(tooltipVizibil==='descarca'?null:'descarca')}>?</button>
                {tooltipVizibil==='descarca' && (
                  <div style={{position:'absolute',right:0,top:32,width:260,background:'#333',color:'white',padding:10,borderRadius:6,fontSize:12,zIndex:10}}>
                    Descarcă un fișier Excel gol, cu toate coloanele necesare pentru a adăuga mai multe documente de tip
                    „{catInfo.label}" deodată. Primul rând e antetul, al doilea explică ce trebuie completat în fiecare
                    coloană. Completează câte un rând pentru fiecare document, apoi încarcă fișierul înapoi cu butonul
                    de mai jos. Vehiculul trebuie să existe deja în aplicație.
                  </div>
                )}
              </span>
            </div>

            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <button type="button" className="btn btn-primary" style={{flex:1}} onClick={incarcaMacheta} disabled={importing}>
                {importing ? 'Se procesează...' : '📤 Încarcă machetă completată'}
              </button>
              <span style={{position:'relative'}}>
                <button type="button" className="btn btn-sm" style={{borderRadius:'50%',width:26,height:26,padding:0}}
                  onClick={()=>setTooltipVizibil(tooltipVizibil==='incarca'?null:'incarca')}>?</button>
                {tooltipVizibil==='incarca' && (
                  <div style={{position:'absolute',right:0,top:32,width:260,background:'#333',color:'white',padding:10,borderRadius:6,fontSize:12,zIndex:10}}>
                    Încarcă fișierul Excel completat. Sistemul verifică fiecare rând — dacă găsește vreo greșeală
                    (câmp obligatoriu gol, text prea lung, dată greșită, vehicul inexistent), nu adaugă niciun
                    document și îți arată exact ce trebuie corectat. Rândurile complet goale sunt ignorate.
                  </div>
                )}
              </span>
            </div>

            {importSuccessMsg && (
              <div style={{background:'#e8f8ee',color:'#1a7a3a',padding:'10px 14px',borderRadius:6,fontSize:13}}>
                ✅ {importSuccessMsg}
              </div>
            )}

            {importErrorMsg && !importErori && (
              <div style={{background:'#fde8e8',color:'#c0392b',padding:'10px 14px',borderRadius:6,fontSize:13}}>
                ❌ {importErrorMsg}
              </div>
            )}

            {importErori && (
              <div style={{background:'#fde8e8',borderRadius:6,padding:'10px 14px'}}>
                <div style={{color:'#c0392b',fontWeight:'bold',fontSize:13,marginBottom:6}}>{importErrorMsg}</div>
                <ul style={{margin:0,paddingLeft:18,maxHeight:220,overflowY:'auto'}}>
                  {importErori.map((er,i)=>(
                    <li key={i} style={{fontSize:12,color:'#721c24',marginBottom:3}}>{er}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}
      {reinnoireModal && (
        <Modal title={`Reînnoire — ${reinnoireModal.tipDocument} (${reinnoireModal.nrInmatriculare})`}
          onClose={()=>setReinnoireModal(null)} onSave={saveReinnoire} saveLabel="Reînnoiește">

          <div style={{background:'#f0f4fa',padding:'8px 12px',borderRadius:4,marginBottom:12,fontSize:12,color:'#555'}}>
            Documentul curent (<b>{reinnoireModal.nrDoc}</b>, expiră {reinnoireModal.dataExpirare}) va fi marcat ca înlocuit.
            Completează datele noului document de mai jos.
          </div>

          <div className="form-row">
            <div className="form-group"><label>Nr. Auto</label>
              <input type="text" value={reinnoireModal.nrInmatriculare} disabled/>
            </div>
            <div className="form-group"><label>Tip document</label>
              <input type="text" value={reinnoireModal.tipDocument} disabled/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Nr. Document nou<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="text" value={reinnoireForm.nrDoc||''} onChange={e=>setReinnoireForm({...reinnoireForm,nrDoc:e.target.value})}/>
            </div>
            <div className="form-group"><label>Valoare (RON)<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={reinnoireForm.valoare||''} onChange={e=>setReinnoireForm({...reinnoireForm,valoare:e.target.value})}/>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Data document<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={reinnoireForm.dataDoc||''} onChange={e=>setReinnoireForm({...reinnoireForm,dataDoc:e.target.value})}/>
            </div>
            <div className="form-group"><label>Data expirare<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={reinnoireForm.dataExpirare||''} onChange={e=>setReinnoireForm({...reinnoireForm,dataExpirare:e.target.value})}/>
            </div>
          </div>
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}
    </div>
  );
}
